import { handleApiError } from "../util/error-handlers";
import { getCaregiverService } from "./api-helper";

import { UpdateChildConfigRequest, UpdateDeviceInfoRequest, UpdateChildInfoRequest, CreateChildRequest, AppSubscriptionType } from "../openapi";

const getChildren = async () => {
  try {
    const caregiverService = await getCaregiverService();
    const childrens = (await caregiverService.getChildren()).data;
    return childrens;
  } catch (error) {
    handleApiError(error);
    return undefined;
  }
};

const deleteChild = async (child: { id: string }) => {
  try {
    const caregiverService = await getCaregiverService();
    const childrens = (await caregiverService.deleteChild(child.id)).data;
    return childrens;
  } catch (error) {
    handleApiError(error);
    throw error;
  }
};

// cancelSubscriptions replaces the whole pending-cancellation state, so the existing
// pending children and Bark cancellation are re-sent along with this child.
const cancelChildSubscription = async (childId: string) => {
  try {
    const caregiverService = await getCaregiverService();
    const { pendingDeleteChildrenIds, subscriptionItems } = (await caregiverService.getBillingInfo()).data;
    const childrenIds = [...pendingDeleteChildrenIds.filter((id) => id !== childId), childId];
    // Truthy check so a null upcomingCancellation doesn't cancel an active Bark subscription
    const barkCancelPending = subscriptionItems.some(
      (item) => item.appSubscriptionType === AppSubscriptionType.Bark && Boolean(item.upcomingCancellation),
    );
    await caregiverService.cancelSubscriptions(childrenIds.join(","), barkCancelPending);
  } catch (error) {
    handleApiError(error);
    throw error;
  }
};

const updateChildConfig = async (childId: string, data: UpdateChildConfigRequest) => {
  try {
    const caregiverService = await getCaregiverService();
    const children = (await caregiverService.updateChildConfig(
      childId,
      data
    )).data;
    return children;
  } catch (error) {
    handleApiError(error);
    return undefined;
  }
};

const updateDeviceInfo = async (childId: string, data: UpdateDeviceInfoRequest) => {
  try {
    const caregiverService = await getCaregiverService();
    const children = (await caregiverService.updateDeviceInfo(
      childId,
      data
    )).data;
    return children;
  } catch (error) {
    handleApiError(error);
    return undefined;
  }
};

const updateChildInfo = async (childId: string, data: UpdateChildInfoRequest) => {
  try {
    const caregiverService = await getCaregiverService();
    const children = (await caregiverService.updateChildInfo(
      childId,
      data
    )).data;
    return children;
  } catch (error) {
    handleApiError(error);
    return undefined;
  }
};

const createChildren = async (data: CreateChildRequest) => {
  try {
    const caregiverService = await getCaregiverService();
    const children = (await caregiverService.createChild(data)).data;
    return children;
  } catch (error) {
    handleApiError(error);
    return undefined;
  }
};

const getCaregiver = async () => {
  try {
    const caregiverService = await getCaregiverService();
    const caregiver = (await caregiverService.getCaregiver()).data;
    return caregiver;
  } catch (error) {
    handleApiError(error);
    return undefined;
  }
};

const signOutChildFromAllDevices = async (childId: string) => {
  try {
    const caregiverService = await getCaregiverService();
    const response = (await caregiverService.signOutGlobally(childId)).data;
    return response;
  } catch (error) {
    handleApiError(error);
    return undefined;
  }
};

export {
  deleteChild,
  cancelChildSubscription,
  updateChildConfig,
  updateDeviceInfo,
  updateChildInfo,
  getChildren,
  createChildren,
  getCaregiver,
  signOutChildFromAllDevices
};
